<script setup lang="ts">
import { getExtraMetadataText, getMetadataText, shouldDisplayMetadataTag } from '@@/bits/attachments'
import { dir } from '@@/bits/current_dir'
import { isAppUsing } from '@@/bits/flip'
import { defineAsyncComponent } from '@@/bits/vue'
import type { PostColor } from '@@/types'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { LinkHelpers } from '@padlet/beethoven-client'
import { computed } from 'vue'

const SurfaceAttachmentMetadataSlope = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceAttachmentMetadataSlope.vue'),
)
const WhiteboardAttachmentMetadataSlope = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardAttachmentMetadataSlope.vue'),
)

const props = withDefaults(
  defineProps<{
    usageContext: 'postModal' | 'postView' | 'postPreview' | 'thumbnailPreview'
    xMetadata?: boolean
    displayAttributes?: BeethovenDisplayAttributes | null
    backgroundColor?: PostColor
    darkThemeType?: 'browser' | 'custom'
    isDark?: boolean
    isWhiteboard?: boolean
  }>(),
  {
    xMetadata: false,
    displayAttributes: null,
    backgroundColor: undefined,
    darkThemeType: 'browser',
    isDark: false,
    isWhiteboard: false,
  },
)

const hasMetadata = computed(() => {
  if (!props.displayAttributes) return false
  return shouldDisplayMetadataTag(props.displayAttributes)
})

const metadataText = computed(() => {
  if (!props.displayAttributes) return ''
  return getMetadataText(props.displayAttributes)
})

const extraMetadataText = computed(() => {
  if (!props.displayAttributes) {
    return ''
  }
  return getExtraMetadataText(props.displayAttributes)
})

const shouldAddColor = computed(() => {
  const isAudioFile = props.displayAttributes != null && LinkHelpers.isUploadedAudio(props.displayAttributes)
  return isAudioFile && isAppUsing('audioPlayerV2')
})
</script>

<template>
  <div :class="['relative']">
    <slot />
    <!-- Note: 100% - 8rem only works when the wrapper has position: relative -->
    <component
      :is="isWhiteboard ? WhiteboardAttachmentMetadataSlope : SurfaceAttachmentMetadataSlope"
      v-if="xMetadata && hasMetadata"
      class="max-w-[calc(100%-2.5rem)]"
      :color="backgroundColor ?? 'default'"
      :dark-theme-type="darkThemeType"
      :is-dark="isDark"
      :should-add-color="shouldAddColor"
      :title="metadataText"
      :usage-context="usageContext"
    >
      <div :dir="dir()" class="truncate font-sans flex flex-row">
        <span class="truncate">{{ metadataText }}</span>
        <template v-if="extraMetadataText">
          <span :class="{ 'text-light-text-300': isDark, 'text-dark-text-300': !isDark, 'px-1': true }"> • </span>
          <span>{{ extraMetadataText }}</span>
        </template>
      </div>
    </component>
  </div>
</template>
